import { useCallback } from 'react';
import { useObservable, useComputed } from '@legendapp/state/react';
import type { CartItemInterface, Maybe } from '../../types/front.graphql';
import type { Observable } from '@legendapp/state';
import { type CheckoutCartStore, checkoutCartStore$ } from '../../stores/checkout-cart.store';

export interface UseCheckoutCartInterface {
    checkoutCart$: Observable<CheckoutCartStore>;
    addItem: (item: Maybe<CartItemInterface>) => void;
    removeItem: (itemId: string) => void;
    updateItem: (itemId: string, changes: Partial<CartItemInterface>) => void;
    getTotalQuantity: () => number;
    getItem: (itemId: string) => Maybe<CartItemInterface> | undefined;
    resetCart: () => void;
    // This is the initial cart setup function, it initializes the cart with a new ID and empty items.
    // The cart id is the real cart id that will be used in the checkout process.
    initCart: (cartId: string) => void;
    getCartId: () => string;
    cartTotalItems: Observable<number>;
    items: Observable<Maybe<CartItemInterface>[]>;
    isItemInCart: (item: CartItemInterface) => boolean;
    getItemsObservable: Observable<Maybe<Maybe<CartItemInterface>[]>>;
}

export function useCheckoutCart(): UseCheckoutCartInterface {
    const checkoutCart$ = useObservable(checkoutCartStore$);

    const addItem = useCallback(
        (item: Maybe<CartItemInterface>) => {
            checkoutCart$.items.set([...(checkoutCart$.items.peek() || []), item]);
        },
        [checkoutCart$]
    );

    const removeItem = useCallback(
        (itemId: string) => {
            checkoutCart$.items.set(
                (checkoutCart$.items.peek() || []).filter((i: Maybe<CartItemInterface>) => i && i.id !== itemId)
            );
        },
        [checkoutCart$]
    );

    const updateItem = useCallback(
        (itemId: string, changes: Partial<CartItemInterface>) => {
            checkoutCart$.items.set(
                (checkoutCart$.items.peek() || []).map((i: Maybe<CartItemInterface>) =>
                    i && i.id === itemId ? { ...i, ...changes } : i
                )
            );
        },
        [checkoutCart$]
    );

    const getTotalQuantity = useCallback(() => {
        return (checkoutCart$.items.peek() || []).reduce(
            (sum: number, i: Maybe<CartItemInterface>) => sum + (i?.quantity || 0),
            0
        );
    }, [checkoutCart$]);

    const getItem = useCallback(
        (itemId: string) => {
            return (checkoutCart$.items.peek() || []).find((i: Maybe<CartItemInterface>) => i && i.id === itemId);
        },
        [checkoutCart$]
    );

    const resetCart = useCallback(() => {
        checkoutCart$.items.set([]);
    }, [checkoutCart$]);

    const initCart = useCallback(
        (cartId: string) => {
            checkoutCart$.set({ id: cartId, items: [] });
        },
        [checkoutCart$]
    );

    const getCartId = useCallback(() => {
        return checkoutCart$.id.peek();
    }, [checkoutCart$]);

    const cartTotalItems = useComputed(() => {
        return (checkoutCart$.items.get() || []).reduce(
            (sum: number, i: Maybe<CartItemInterface>) => sum + (i?.quantity || 0),
            0
        );
    });

    const items = useComputed(() => {
        return (checkoutCart$.items.get() || []).filter(Boolean);
    });

    const isItemInCart = (item: CartItemInterface) => {
        return (checkoutCart$.items.peek() || []).some(
            (i: Maybe<CartItemInterface>) => i && i.product?.sku === (item?.product?.sku || 'no-sku')
        );
    };

    const getItemsObservable = useComputed(() => {
        return checkoutCart$.items.get();
    });

    return {
        checkoutCart$: checkoutCart$,
        addItem,
        removeItem,
        updateItem,
        getTotalQuantity,
        getItem,
        resetCart,
        initCart,
        getCartId,
        cartTotalItems,
        items,
        isItemInCart: isItemInCart,
        getItemsObservable,
    };
}
